import constants from '../constants';
import { getElementId, getElementSizes, hasLockedParent, isMarkedForAdjacentPage } from '../../helpers';
import { Api } from '../../../../../typings/api';
import { DuplicatedItemInSpreadConfig } from '../html';

export interface SlideData extends Api.AspDriveResourceItemLight {
	id: string;
	slideText?: SlideTextData;
}

export interface SlideTextData {
	value: string;
	position: SlideTextPosition;
	font: SlideTextFont;
}

export interface SlideTextPosition {
	x: SlideTextXPosition;
	y: SlideTextYPosition;
}

export enum SlideTextXPosition {
	LEFT = 'LEFT',
	CENTER = 'CENTER',
	RIGHT = 'RIGHT',
}

export enum SlideTextYPosition {
	TOP = 'TOP',
	CENTER = 'CENTER',
	BOTTOM = 'BOTTOM',
	ABOVE_SLIDE = 'ABOVE_SLIDE',
	UNDER_SLIDE = 'UNDER_SLIDE',
}

interface SlideTextFont {
	family: string;
	size: number; // px
	color: string; // rgba
	backgroundColor?: string; // rgba
	shadowColor?: string; // rgba
}

export interface ImportingSlideData extends SlideData {
	file?: File;
}

export interface SlidersConfig {
	[key: string]: SlideData[] | {
		slides: SlideData[];
		settings?: SliderSettingsData;
	}; // new format after v3.55
}

interface SliderSettingsData {
	scaleImage?: boolean;
}

export interface SliderConfig {
	id: string;
	idInConfigs: string;
	className: string;
	page: number;
	isVisible: boolean;
	deps: string[];
	importingSlides: ImportingSlideData[];
	width: number;
	height: number;
	isEditedFromBackoffice?: boolean;
}

interface SlidersData {
	items: SliderConfig[];
	duplicatedItemsInSpread: DuplicatedItemInSpreadConfig[];
	isError: boolean;
}

export enum SliderElementDataAttribute {
	hidden = 'data-hidden', // is set when slider is hidden
	deps = 'data-deps', // is set to element to allow a server to add slider resources to publication dependencies
}

// every slider the plugin ever exported starts with one of these, old exports included. A dynamic content slider
// (_<id>-plugin-slider) only ends with the name and has no entry in pagesData[].sliders, so there is nothing to edit
const sliderClassNamePrefixes = [
	`${constants.sliderClassName}-`,
	`${constants.editableSliderClassName}-`,
];

const getSliderClassName = (element: HTMLElement): string | undefined => (
	element.className.split(' ').find(className => (
		sliderClassNamePrefixes.some(prefix => className.indexOf(prefix) === 0)
	))
);

const getConfig = (element: HTMLDivElement, page: number, css: string): SliderConfig | null => {
	const filteredClassName = getSliderClassName(element);

	// a dynamic content slider, see sliderClassNamePrefixes
	if (!filteredClassName) {
		return null;
	}

	const id = getElementId(element);

	const isVisible = !(element.getAttribute(SliderElementDataAttribute.hidden) &&
		element.getAttribute(SliderElementDataAttribute.hidden) === 'true');
	const depsAttr = element.getAttribute(SliderElementDataAttribute.deps);
	const deps = depsAttr ? depsAttr.split(';') : [];
	const { width, height } = getElementSizes(id, css);

	return {
		page,
		id,
		idInConfigs: filteredClassName,
		className: filteredClassName,
		isVisible,
		deps,
		importingSlides: [],
		width,
		height,
	};
};

export default (page: number, pageIndexInSpread: number, htmlDocument: Document, css: string): SlidersData => {
	const items: SliderConfig[] = [];
	const duplicatedItemsInSpread: DuplicatedItemInSpreadConfig[] = [];
	let isError: boolean = false;

	const sliderElements = ([
		...htmlDocument.querySelectorAll(`[class*=${constants.sliderClassName}]`),
	] as HTMLDivElement[]).filter(element => !hasLockedParent(element));

	try {
		sliderElements.forEach((element) => {
			const config = getConfig(element, page, css);

			if (!config) {
				return;
			}

			items.push(config);

			if (isMarkedForAdjacentPage(config.className, pageIndexInSpread)) {
				duplicatedItemsInSpread.push({ page, id: config.id, className: config.className });
			}
		});
	} catch (err) {
		isError = true;
	}

	return {
		items,
		duplicatedItemsInSpread,
		isError,
	};
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/utils/indesignEditor/editableSliders/getData.ts